import { invert } from 'radash';

import { ProjectStatus } from 'src/constants/graphql-enums';

import type { LocationCriteria } from 'containers/Booking/types';

// Booking related constants
const BOOKING_STATUS = {
  DRAFT: 0,
  ACTIVE: 1,
  PENDING_REVIEW: 2,
  IN_REVIEW: 3,
  REJECTED: 4,
  COMPLETED: 5,
  PENDING_PAYMENT: 6,
  ARCHIVED: 7,
} as const;

const BOOKING_STATUS_VALUE_TO_KEY = invert(BOOKING_STATUS);

const BOOKING_TYPE = {
  IN_PERSON: 1,
  REMOTE: 2,
  ONLINE: 3,
  LONGITUDINAL_STUDY: 4,
  UNMODERATED: 5,
  AI_MODERATED: 6,
  SURVEY: 7,
} as const;

// Study types that use the task-block-based builder (Build, Preview, Results with task blocks)
const TASK_BASED_STUDY_TYPES = [BOOKING_TYPE.UNMODERATED, BOOKING_TYPE.SURVEY] as const;

// Study types that can enable account-less ("Send a link") BYO recruitment. Matches the set the
// graphql API accepts, so UI and API agree on eligibility.
const BYO_ELIGIBLE_STUDY_TYPES: readonly number[] = [
  BOOKING_TYPE.SURVEY,
  BOOKING_TYPE.UNMODERATED,
  BOOKING_TYPE.AI_MODERATED,
];

const BOOKING_SUBTYPE = {
  ONLINE_TASK: 1,
  SURVEY: 2,
} as const;

/** `config.session.type`. */
const BOOKING_SESSION_TYPE = {
  ONE_TO_ONE: 1,
  GROUP: 2,
} as const;

const BOOKING_SESSION_STATUS = {
  LIVE: 1,
  CANCELLED_BY_ADMIN: 2,
  CANCELLED_BY_CLIENT: 3,
} as const;

const BOOKING_CONFIG_PARTICIPANT_AGREEMENT_TYPE = {
  ASKABLE_TERMS: 0,
  CUSTOM_AGREEMENT: 1,
} as const;

const BOOKING_ESIGNATURE_TEMPLATE_TYPE = {
  PARTICIPANT_AGREEMENT: 1,
} as const;

// Submission related constants
const SUBMISSION_AGREEMENT_STATUS = {
  CONSENT_GIVEN: 1,
  CONSENT_DENIED: 2,
  SENT: 3,
  VIEWED: 4,
  DECLINED: 5,
  COMPLETED: 6,
} as const;

const SUBMISSION_SESSION_STATUS = {
  CONFIRMED: 1,
  WAITLISTED: 2,
  AVAILABLE: 3,
  INVITED: 4,
  IN_PROGRESS: 5,
} as const;

/** Who or what cancelled a submission's session row. */
const SUBMISSION_SESSION_CANCEL = {
  NOT_CANCELLED: 0,
  BY_ADMIN: 1,
  BY_CLIENT: 2,
  BY_PARTICIPANT: 3,
  NO_SHOW: 4,
  BY_SYSTEM: 5,
  RESCHEDULED: 6,
  DUE_TO_ISSUE: 7,
} as const;

// Message related constants
const MESSAGE_DIRECTION = {
  OTHER: 0, // other or unknown - legacy only
  ASKABLE_TO_PARTICIPANT: 1,
  ASKABLE_TO_CLIENT: 2,
  CLIENT_TO_PARTICIPANT: 3,
  PARTICIPANT_TO_CLIENT: 4,
  ASKABLE_TO_OTHER: 5,
  PARTICIPANT_TO_ASKABLE: 6,
} as const;

// Credit related constants
const CREDIT_ACTIVITY_TYPE = {
  ADMIN_MANUAL: 0,
  CREDIT_USAGE: 1,
  CREDIT_PURCHASE: 2,
  REFUND_NOSHOW: 3,
  REFUND_UNFULIILLED: 4,
  REFUND_CANCELLED_REJECTED: 5,
  REFUND_BAD_PARTICIPANT: 6,
  REFUND_OTHER: 7,
  BOOKING_REQUIREMENT_ADJUSTMENT: 8,
  FREE_PROMOTIONAL: 9,
} as const;

// Project related constants
const PROJECT_STATUS_KEY_VALUE: Record<ProjectStatus, number> = {
  [ProjectStatus.Draft]: 0,
  [ProjectStatus.Active]: 1,
  [ProjectStatus.Completed]: 5,
  [ProjectStatus.Archived]: 7,
} as const;

const PROJECT_STATUS_VALUE_KEY = invert(PROJECT_STATUS_KEY_VALUE);

const PROJECT_TYPE = {
  // As every study has a _project_id, type is the way to differentiate between standalone study and askable plus study
  // If type is 0, it's a standalone study
  NONE: 0,
  // If type is 1, it's an askable plus study
  ASKABLE_PLUS: 1,
} as const;

// Notification related constants
const NOTIFICATION_TYPE = {
  BOOKING: 1,
  BOOKING_PARTICIPANT: 2,
  TEAM: 3,
  MESSAGES: 4,
} as const;

const NOTIFICATION_SUBTYPE = {
  // Booking type
  BOOKING_APPROVED: 1,
  // Booking Participant type
  NEW_APPLICANTS: 1,
  BOOKING_PARTICIPANT_CANCELLED: 2,
  BOOKING_PARTICIPANT_FEEDBACK: 3,
  BOOKING_PARTICIPANT_CONFIRMS: 4,
  BOOKING_PARTICIPANT_COMPLETED: 5,
  // Team type
  USER_JOINED_TEAM: 1,
  // Messages type
  NEW_MESSAGE_FROM_PARTICIPANT: 1,
} as const;

// Device and quota constants
const REQUIRED_DEVICE_TYPE = {
  DESKTOP: 1,
  MOBILE: 2,
  ANY: '',
} as const;

const DEFAULT_QUOTA = {
  IN_PERSON: 10,
  REMOTE: 12,
  ONLINE_TASK: 50,
  SURVEY: 100,
  LONGITUDINAL_STUDY: 10,
  AI_MODERATED: 30,
} as const;

// Location related constants
const LOCATIONS_KEYS: (keyof Pick<LocationCriteria, 'countries' | 'states' | 'bounds'>)[] = [
  'bounds',
  'states',
  'countries',
];

export {
  BOOKING_CONFIG_PARTICIPANT_AGREEMENT_TYPE,
  BOOKING_ESIGNATURE_TEMPLATE_TYPE,
  BOOKING_SESSION_STATUS,
  BOOKING_SESSION_TYPE,
  BOOKING_STATUS,
  BOOKING_STATUS_VALUE_TO_KEY,
  BOOKING_SUBTYPE,
  BOOKING_TYPE,
  BYO_ELIGIBLE_STUDY_TYPES,
  CREDIT_ACTIVITY_TYPE,
  DEFAULT_QUOTA,
  LOCATIONS_KEYS,
  MESSAGE_DIRECTION,
  NOTIFICATION_SUBTYPE,
  NOTIFICATION_TYPE,
  PROJECT_STATUS_KEY_VALUE,
  PROJECT_STATUS_VALUE_KEY,
  PROJECT_TYPE,
  REQUIRED_DEVICE_TYPE,
  SUBMISSION_AGREEMENT_STATUS,
  SUBMISSION_SESSION_CANCEL,
  SUBMISSION_SESSION_STATUS,
  TASK_BASED_STUDY_TYPES,
};
